import React, { useEffect } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { LineChartOptions } from './types';
import multiLineDataParser from './multiLineDataParser';

const getTooltipFormatter = (categories?: Array<string | number | null | undefined>): Highcharts.TooltipFormatterCallbackFunction =>
  function tooltipFormatter(this: Highcharts.TooltipFormatterContextObject) {
    const pointIndex = this.point?.index ?? 0;
    const xValue = categories?.[pointIndex] ?? this.x;
    const rawY = typeof this.y === 'number' ? this.y : 0;
    const yValue = rawY < 0 ? 0 : rawY;
    const pointText = 'Rating';

    return `${xValue}: ${yValue} ${pointText}`;
  };

const LineChart = ({ title, xAxis, yAxis, data, modifiedTooltip, multiLine = false, legend = false, maxTick, minTick = 0, customTooltipFormatter, tickAmount = 6 }: LineChartOptions) => {
  const [graphData, setGraphData] = React.useState<any[]>([[]]);
  const tooltipLockedRef = React.useRef(false);
  const chartRef = React.useRef<Highcharts.Chart | null>(null);
  const tooltipElementRef = React.useRef<HTMLElement | null>(null);
  const handlersRef = React.useRef<{ mouseEnter?: () => void; mouseLeave?: () => void }>({});

  useEffect(() => {
    if (Array.isArray(data)) {
      const seriesData = multiLineDataParser(data, multiLine);
      setGraphData(seriesData);
    }
  }, [data, multiLine]);

  // Cleanup function to remove event listeners
  const cleanupTooltipListeners = React.useCallback(() => {
    if (tooltipElementRef.current && handlersRef.current.mouseEnter && handlersRef.current.mouseLeave) {
      const element = tooltipElementRef.current;
      element.removeEventListener('mouseenter', handlersRef.current.mouseEnter);
      element.removeEventListener('mouseleave', handlersRef.current.mouseLeave);
      tooltipElementRef.current = null;
      handlersRef.current = {};
    }
  }, []);

  const attachTooltipListeners = React.useCallback(() => {
    if (chartRef.current) {
      const { tooltip } = chartRef.current;
      const tooltipElement = (tooltip as any)?.label?.element;

      if (tooltipElement && tooltipElement !== tooltipElementRef.current) {
        // Clean up old listeners if element changed
        cleanupTooltipListeners();

        const handleMouseEnter = () => {
          tooltipLockedRef.current = true;
        };

        const handleMouseLeave = () => {
          tooltipLockedRef.current = false;
          if (chartRef.current) {
            chartRef.current.tooltip?.hide();
          }
        };

        tooltipElement.addEventListener('mouseenter', handleMouseEnter);
        tooltipElement.addEventListener('mouseleave', handleMouseLeave);

        // Store handler references for cleanup
        handlersRef.current = { mouseEnter: handleMouseEnter, mouseLeave: handleMouseLeave };
        tooltipElementRef.current = tooltipElement;
      }
    }
  }, [cleanupTooltipListeners]);

  // Cleanup effect on unmount
  useEffect(() => {
    return () => {
      cleanupTooltipListeners();
    };
  }, [cleanupTooltipListeners]);

  const options: Highcharts.Options = {
    chart: {
      height: 390,
      width: 490,
      style: {
        fontFamily: 'Inter',
        fontSize: '14.137px',
        fontWeight: '500',
        color: '#838383',
      },
      events: {
        load() {
          chartRef.current = this;
          attachTooltipListeners();
        },
        render() {
          attachTooltipListeners();
        },
      },
    },
    tooltip: modifiedTooltip
      ? {
          formatter: customTooltipFormatter
            ? function (this: Highcharts.TooltipFormatterContextObject) {
                return customTooltipFormatter.call(this, this);
              }
            : getTooltipFormatter(xAxis?.categories),
          useHTML: !!customTooltipFormatter,
          hideDelay: 500,
          outside: false,
          stickOnContact: true,
        }
      : {},
    credits: {
      enabled: false,
    },
    legend: {
      enabled: legend,
    },
    title: {
      text: title,
    },
    xAxis: {
      labels: {
        style: {
          color: '#838383',
          textAlign: 'center',
          fontFamily: 'Inter',
          fontSize: '14.137px',
          fontStyle: 'normal',
          fontWeight: '500',
          lineHeight: 'normal',
        },
      },
      categories: xAxis?.categories,
    },
    yAxis: {
      labels: {
        style: {
          color: '#000',
          textAlign: 'right',
          fontFamily: 'Inter',
          fontSize: '15.309px',
          fontStyle: 'normal',
          fontWeight: '500',
          lineHeight: '19.811px',
        },
      },
      title: {
        text: yAxis?.title?.text,
      },
      min: minTick,
      max: maxTick,
      tickAmount,
    },
    series: graphData.map((dataSeries, index) => ({
      type: 'line',
      name: dataSeries?.name || `Series ${index + 1}`,
      data: dataSeries?.data?.map((value: number, dataIndex: number) => [xAxis?.categories?.[dataIndex], value]) || [],
      color: ['#2559C3', '#FF5733', '#33FF57', '#FF33A8'][index % 4],
      marker: {
        fillColor: '#FFFFFF',
        lineWidth: 2,
        lineColor: ['#2559C3', '#FF5733', '#33FF57', '#FF33A8'][index % 4], // inherit from series
      },
      point: {
        events: {
          mouseOver(e: any) {
            if (!tooltipLockedRef.current && e.target.series?.chart?.tooltip) {
              e.target.series.chart.tooltip.refresh(e.target);
            }
          },
        },
      },
    })),
  };

  return (
    <div className="score-chart-inner-wrap">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

LineChart.defaultProps = {
  xAxis: {
    categories: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
  },
  yAxis: {
    title: {
      text: 'Value',
    },
  },
  data: [1, 3, 2, 4, 5, 7, 6, 8, 9, 10, 8, 7],
};

export default LineChart;
